import { NotificationData } from '@/common/types/notifications'
import { cn } from '@/common/utils/cn'
import { getDateDiffFormatted } from '@/common/utils/date'
import { DynamicIcon, iconNames } from 'lucide-react/dynamic'
import { useEffect, useRef } from 'react'
import { Tooltip } from 'react-tooltip'

interface NotificationProps {
  id: string
  content: NotificationData['content']
  date: Date
  seenAt: string | null
  onVisibilityChange?: (id: string, element: Element) => void
  notificationsToRead: string[]
}

export const Notification = ({ id, content, date, seenAt, onVisibilityChange, notificationsToRead }: NotificationProps) => {
  const notificationRef = useRef<HTMLDivElement>(null)
  const thisNotificationIsRead = notificationsToRead.find((notificationId) => notificationId === id)

  useEffect(() => {
    if (!seenAt && !thisNotificationIsRead && notificationRef.current && onVisibilityChange) {
      onVisibilityChange(id, notificationRef.current)
    }
  }, [id, seenAt, onVisibilityChange])

  return (
    <div
      ref={notificationRef}
      className={cn('p-4 flex gap-x-2', {
        'bg-yellow-op-2': !seenAt && !thisNotificationIsRead,
      })}
    >
      <div className='w-9'>
        <DynamicIcon
          name={iconNames.includes(content.icon) ? content.icon : 'message-square'}
          color={content.icon_color}
          strokeWidth={1}
          className="size-8"
        />
      </div>

      <div className="w-full space-y-2">
        <div className="flex items-center justify-between">
          <p data-tooltip-id={`title-${id}`} className="w-56 text-sm font-bold truncate">
            {content.title}
          </p>

          {date && <p className="text-xs font-light italic text-nowrap">Há {getDateDiffFormatted(new Date(), date)}</p>}
        </div>

        {!!content.body?.length && (
          <>
            {Array.isArray(content.body) ? (
              Array.isArray(content.body[0]) ? (
                <div data-tooltip-id={`body-${id}`} data-tooltip-place="right-end">
                  {content.body[0].map((text: string, index: number) => (
                    <p key={index} className="text-xs">
                      {text}
                    </p>
                  ))}

                  {content.body?.length > 1 && <p className="text-xs text-blue-800">Ver mais...</p>}
                </div>
              ) : (
                <p data-tooltip-id={`body-${id}`} data-tooltip-place="right-end" className="text-xs">
                  {content.body[0]}
                </p>
              )
            ) : (
              <p className="text-xs">{content.body}</p>
            )}
          </>
        )}
      </div>

      <Tooltip id={`title-${id}`}>{content.title}</Tooltip>

      {Array.isArray(content.body) && content.body.length > 0 && (
        <Tooltip id={`body-${id}`}>
          {Array.isArray(content.body[0]) ? (
            <div className="flex flex-col">
              {content.body.map((lines, linesIndex) => (
                <div key={`body-${id}-${linesIndex}`}>
                  {linesIndex > 0 && <div className="h-px w-full bg-white-100 my-1" />}

                  {(lines as string[]).map((line, lineIndex) => (
                    <p key={`body-${id}-${linesIndex}-${lineIndex}`} className="text-xs">
                      {line}
                    </p>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            content.body.map((line, index) => (
              <p key={index} className="text-xs">
                {line}
              </p>
            ))
          )}
        </Tooltip>
      )}
    </div>
  )
}
